/**
 * Splits a template description into the one line worth showing inline and the
 * rest, which goes behind the option's info popover.
 *
 * The descriptions are backend copy from the export-templates config and run to
 * a full paragraph each — five of them stacked is taller than the drawer, so
 * the setup zone can only carry the opening sentence.
 */
export interface TemplateSummary {
  /** The opening sentence, rendered under the option label. */
  summary: string;
  /** Everything after it, or `null` when the description is a single sentence. */
  detail: string | null;
}

/** A sentence ends at a period followed by whitespace and a new sentence's
 *  first character — a capital or an opening backtick, since the copy starts
 *  sentences with identifiers like `` `id` carries… ``. Requiring that lookahead
 *  is what keeps `custom_label_0..4`, `11,337` and `e.g.` from splitting. */
const SENTENCE_END = /\.\s+(?=[A-Z`])/;

/** Splits one description; see `TemplateSummary`. Whitespace-only detail is
 *  reported as no detail, so the popover never opens on nothing. */
export function summarizeTemplateDescription(description: string): TemplateSummary {
  const match = SENTENCE_END.exec(description);
  if (!match) {
    return { summary: description.trim(), detail: null };
  }

  const summary = description.slice(0, match.index + 1).trim();
  const detail = description.slice(match.index + match[0].length).trim();
  return { summary, detail: detail === '' ? null : detail };
}
